import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/datenschutz')({
  component: DatenschutzPage,
});

function DatenschutzPage() {
  return (
    <>
      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroSlideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes gradientShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .dsg-hero-bg {
          background: linear-gradient(160deg, #1d1d1b 0%, #2c1a19 50%, #1d1d1b 100%);
          background-size: 200% 200%;
          animation: gradientShift 14s ease infinite;
        }
        .dsg-eyebrow {
          animation: heroSlideIn 0.6s ease-out forwards;
          opacity: 0;
        }
        .dsg-title {
          animation: heroFadeUp 0.7s ease-out 0.15s forwards;
          opacity: 0;
        }
        .dsg-subtitle {
          animation: heroFadeUp 0.6s ease-out 0.3s forwards;
          opacity: 0;
        }

        .dsg-gradient-text {
          background: linear-gradient(135deg, #ffffff 0%, #c96b66 55%, #b84e48 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .dsg-content {
          background: #ffffff;
          border: 1px solid rgba(29,29,27,0.08);
          padding: 2.5rem;
        }
        @media (min-width: 640px) {
          .dsg-content {
            padding: 3rem;
          }
        }

        .dsg-section {
          border-left: 3px solid #b84e48;
          padding-left: 1.25rem;
          margin-top: 2rem;
        }

        .dsg-section-title {
          font-size: 1rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #1d1d1b;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .dsg-section-num {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          background: #b84e48;
          color: #ffffff;
          font-size: 0.65rem;
          font-weight: 900;
          flex-shrink: 0;
        }

        .dsg-text {
          font-size: 0.9375rem;
          line-height: 1.75;
          color: rgba(29,29,27,0.7);
          margin-bottom: 0.75rem;
        }
        .dsg-text:last-child {
          margin-bottom: 0;
        }

        .dsg-sub-item {
          padding: 0.75rem 1rem;
          background: rgba(29,29,27,0.02);
          border: 1px solid rgba(29,29,27,0.06);
          margin-bottom: 0.75rem;
        }
        .dsg-sub-item-title {
          font-size: 0.875rem;
          font-weight: 900;
          color: #1d1d1b;
          margin-bottom: 0.375rem;
        }
        .dsg-sub-item-text {
          font-size: 0.875rem;
          line-height: 1.65;
          color: rgba(29,29,27,0.6);
        }

        .dsg-link {
          color: #b84e48;
          text-decoration: none;
          font-weight: 700;
          transition: color 0.15s ease;
        }
        .dsg-link:hover {
          color: #c96b66;
          text-decoration: underline;
        }
      `}</style>

      <div className="min-h-screen bg-paper">
        {/* Hero */}
        <div className="dsg-hero-bg relative overflow-hidden px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
          <div
            className="pointer-events-none absolute inset-0 texture-halftone"
            aria-hidden="true"
          />
          <div
            className="absolute left-0 bottom-0 top-0 w-1 bg-accent opacity-60"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
            aria-hidden="true"
          >
            <span className="select-none text-[clamp(60px,12vw,150px)] font-black uppercase tracking-widest text-white/[0.025] leading-none whitespace-nowrap">
              DATENSCHUTZ
            </span>
          </div>

          <div className="relative mx-auto max-w-3xl">
            <div className="dsg-eyebrow mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-accent" aria-hidden="true" />
              <span className="text-xs font-black uppercase tracking-[0.3em] text-accent">
                Faninitiative Spandau e.V.
              </span>
            </div>
            <h1 className="dsg-title text-[clamp(2.25rem,6vw,4.5rem)] font-black uppercase leading-[0.9] tracking-tight text-white">
              Daten-
              <br />
              <span className="dsg-gradient-text">schutz</span>
            </h1>
            <p className="dsg-subtitle mt-5 text-sm text-white/45 sm:text-base">
              Informationen zur Datenverarbeitung gemäß DSGVO
            </p>
          </div>
        </div>

        {/* Inhalt */}
        <div className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <div className="dsg-content">
              <p className="dsg-text">
                Wir freuen uns sehr über Ihr Interesse an unserem Unternehmen. Datenschutz hat einen
                besonders hohen Stellenwert für die Faninitiative Spandau e.V. Eine Nutzung der
                Internetseiten ist grundsätzlich ohne jede Angabe personenbezogener Daten möglich.
              </p>

              {/* 1. Begriffsbestimmungen */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">1</span>
                  Begriffsbestimmungen
                </div>
                <p className="dsg-text">
                  Wir verwenden in dieser Datenschutzerklärung unter anderem folgende Begriffe:
                </p>
                {[
                  {
                    key: 'a) Personenbezogene Daten',
                    text: 'Alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen.',
                  },
                  {
                    key: 'b) Betroffene Person',
                    text: 'Jede identifizierte oder identifizierbare natürliche Person, deren personenbezogene Daten verarbeitet werden.',
                  },
                  {
                    key: 'c) Verarbeitung',
                    text: 'Jeder Vorgang im Zusammenhang mit personenbezogenen Daten wie Erheben, Speichern, Anpassen, Abfragen, Offenlegung oder Löschen.',
                  },
                  {
                    key: 'k) Einwilligung',
                    text: 'Jede freiwillig und in informierter Weise abgegebene Willensbekundung der betroffenen Person.',
                  },
                ].map(({ key, text }) => (
                  <div key={key} className="dsg-sub-item">
                    <div className="dsg-sub-item-title">{key}</div>
                    <div className="dsg-sub-item-text">{text}</div>
                  </div>
                ))}
              </div>

              {/* 2. Verantwortlicher */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">2</span>
                  Verantwortlicher
                </div>
                <div className="dsg-sub-item">
                  <div className="dsg-sub-item-text">
                    Faninitiative Spandau e.V.
                    <br />
                    Kolonnenstr. 8, 10827 Berlin, Deutschland
                    <br />
                    Tel.: 016090305525
                    <br />
                    E-Mail:{' '}
                    <a href="mailto:glenn-odya@fanini.live" className="dsg-link">
                      glenn-odya@fanini.live
                    </a>
                    <br />
                    Website: www.fanini.live
                  </div>
                </div>
              </div>

              {/* 3. Cookies */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">3</span>
                  Cookies
                </div>
                <p className="dsg-text">
                  Die Internetseiten der Faninitiative Spandau e.V. verwenden Cookies. Cookies sind
                  Textdateien, welche über einen Internetbrowser auf einem Computersystem abgelegt
                  und gespeichert werden. Die betroffene Person kann die Setzung von Cookies
                  jederzeit durch eine entsprechende Einstellung des genutzten Internetbrowsers
                  verhindern.
                </p>
              </div>

              {/* 4. Allgemeine Daten */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">4</span>
                  Erfassung allgemeiner Daten
                </div>
                <p className="dsg-text">
                  Die Internetseite erfasst mit jedem Aufruf eine Reihe allgemeiner Daten:
                  Browsertypen, Betriebssystem, Referrer, Unterwebseiten, Datum und Uhrzeit des
                  Zugriffs sowie IP-Adresse. Diese Informationen werden benötigt, um Inhalte korrekt
                  auszuliefern und die Funktionsfähigkeit der Systeme zu gewährleisten. Es werden
                  keine Rückschlüsse auf die betroffene Person gezogen.
                </p>
              </div>

              {/* 5. Kontaktmöglichkeit */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">5</span>
                  Kontaktmöglichkeit
                </div>
                <p className="dsg-text">
                  Sofern eine betroffene Person per E-Mail Kontakt aufnimmt, werden die
                  übermittelten personenbezogenen Daten automatisch gespeichert. Es erfolgt keine
                  Weitergabe an Dritte.
                </p>
              </div>

              {/* 6. Speicherung */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">6</span>
                  Löschung personenbezogener Daten
                </div>
                <p className="dsg-text">
                  Personenbezogene Daten werden nur für den Zeitraum gespeichert, der zur Erreichung
                  des Speicherungszwecks erforderlich ist. Nach Ablauf der gesetzlichen Frist werden
                  die Daten routinemäßig gelöscht.
                </p>
              </div>

              {/* 7. Rechte */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">7</span>
                  Rechte der betroffenen Person
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    'Recht auf Bestätigung',
                    'Recht auf Auskunft',
                    'Recht auf Berichtigung',
                    'Recht auf Löschung',
                    'Recht auf Einschränkung der Verarbeitung',
                    'Recht auf Datenübertragbarkeit',
                    'Recht auf Widerspruch',
                    'Recht auf Widerruf einer Einwilligung',
                  ].map((right) => (
                    <div
                      key={right}
                      className="flex items-center gap-2 text-sm font-bold text-ink/70 py-1"
                    >
                      <span className="h-1.5 w-1.5 bg-accent flex-shrink-0" aria-hidden="true" />
                      {right}
                    </div>
                  ))}
                </div>
              </div>

              {/* 8. Webanalyse */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">8</span>
                  Webanalyse-Tools und Drittanbieter
                </div>
                <p className="dsg-text">
                  Auf dieser Website werden derzeit keine Webanalyse-Dienste wie Google Analytics
                  oder vergleichbare Tracking-Tools eingesetzt. Es sind keine Social-Media-Plugins
                  direkt integriert — es wird lediglich auf die Social-Media-Profile des Vereins
                  verlinkt.
                </p>
              </div>

              {/* 9. Clerk */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">9</span>
                  Authentifizierung via Clerk
                </div>
                <p className="dsg-text">
                  Diese Website nutzt Clerk (Betreiber: Clerk, Inc., San Francisco, USA) zur
                  Benutzeranmeldung. Bei der Registrierung und Anmeldung werden personenbezogene
                  Daten (z.B. E-Mail-Adresse, Name) an Clerk übermittelt. Die Verarbeitung erfolgt
                  auf Grundlage von Art. 6 Abs. 1 lit. b DS-GVO. Weitere Informationen:{' '}
                  <a
                    href="https://clerk.com/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dsg-link"
                  >
                    clerk.com/legal/privacy
                  </a>
                  .
                </p>
              </div>

              {/* 10. Rechtsgrundlage */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">10</span>
                  Rechtsgrundlage der Verarbeitung
                </div>
                <p className="dsg-text">
                  Art. 6 I lit. a DS-GVO dient als Rechtsgrundlage für Verarbeitungen, bei denen wir
                  eine Einwilligung einholen. Art. 6 I lit. b DS-GVO gilt für Verarbeitungen zur
                  Vertragserfüllung. Art. 6 I lit. c DS-GVO bei rechtlichen Verpflichtungen. Art. 6
                  I lit. f DS-GVO bei berechtigten Interessen.
                </p>
              </div>

              {/* 11. Speicherdauer */}
              <div className="dsg-section">
                <div className="dsg-section-title">
                  <span className="dsg-section-num">11</span>
                  Dauer der Speicherung
                </div>
                <p className="dsg-text">
                  Das Kriterium für die Dauer der Speicherung ist die jeweilige gesetzliche
                  Aufbewahrungsfrist. Nach Ablauf der Frist werden die entsprechenden Daten
                  routinemäßig gelöscht, sofern sie nicht mehr zur Vertragserfüllung erforderlich
                  sind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
